import React, { useState } from 'react';
import { EditableCell } from './QuotationEditableCell';
import { needsConfirmation } from '../../utils/matchReasons';

export const NOTE_PLACEHOLDER = 'Click to add an internal note (not exported)';

type NoteLine = Parameters<typeof needsConfirmation>[0] & { internalRemarks?: string | null };

/**
 * D104.18: a line's internal note. Generation writes a note on almost every line ("Manual review
 * recommended", "[fresh item]", ...), so showing them all buries the few that need action. A note is
 * shown by default only when its line needs confirmation - the SAME `needsConfirmation` the banner
 * counts - and every other note sits behind a per-line "Details" toggle. Nothing is dropped.
 * A line with no note shows the add-a-note prompt, as before.
 */
export const LineNoteCell: React.FC<{
  item: NoteLine;
  onSave: (value: string) => Promise<void>;
}> = ({ item, onSave }) => {
  const [open, setOpen] = useState(false);
  const note = (item.internalRemarks || '').trim();
  const showNote = note === '' || needsConfirmation(item) || open;
  const cell = (
    <EditableCell
      value={note !== '' ? item.internalRemarks! : NOTE_PLACEHOLDER}
      onSave={(v) => onSave(v === NOTE_PLACEHOLDER ? '' : String(v))}
      truncate
      className="w-full text-left min-h-[20px] p-0.5"
    />
  );
  if (showNote) {
    return (
      <div className="flex w-full min-w-0 items-center gap-1">
        <div className="min-w-0 flex-1">{cell}</div>
        {note !== '' && open && !needsConfirmation(item) && (
          <button type="button" className="shrink-0 text-xs text-slate-500 underline" onClick={() => setOpen(false)}>
            Hide
          </button>
        )}
      </div>
    );
  }
  return (
    <button
      type="button"
      className="text-xs text-slate-500 underline"
      onClick={() => setOpen(true)}
      aria-label="Show line notes"
    >
      Details
    </button>
  );
};

export default LineNoteCell;
